import { useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "@/lib/cn"
import type { Block, CollapsibleBlock, ActionPayload, RefreshEnvelope } from "../types"

type Props = {
  block: CollapsibleBlock
  // Recursive — passed in from the parent renderer to avoid a cycle.
  renderBlock: (block: Block) => React.ReactNode
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<RefreshEnvelope | null>
}

// Foldable section. `default_open` is honored at md+ but defaults
// closed at xs/sm to avoid swamping the operator with content on a
// narrow pane (per plan reflow rules). The CSS handles the breakpoint
// flip via a data attribute set by `useDocumentBreakpoint`.
export function CollapsibleBlockView({ block, renderBlock }: Props) {
  // Initial state mirrors `default_open`. The container-query CSS
  // *visually* clamps at xs/sm via `max-height: 0` on a wrapper, but
  // we keep the logical state honest so toggling works on every breakpoint.
  const [open, setOpen] = useState(block.default_open !== false)
  const blocks = Array.isArray(block.blocks) ? block.blocks : []

  return (
    <section
      className="valet-uidoc-collapsible rounded-card border border-border bg-app-panel2/30"
      data-default-open={block.default_open !== false ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-card px-3 py-2 text-left text-sm font-semibold text-app-text",
          "hover:bg-white/5",
        )}
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {block.title}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          {blocks.map((b, idx) => (
            <div key={idx}>{renderBlock(b)}</div>
          ))}
        </div>
      )}
    </section>
  )
}
